/**
=========================================================
* Trust Cargo - Orders (the Follow up sheet)
=========================================================

* Reads the v2 database, not the legacy one.
*
* This page used to call /api/orders, which returns {"orders":[]} - the
* legacy table is empty, so the screen was blank no matter what. Orders now
* live in v2 (589 of them, imported from the workbook), and that is also
* where the create dialog writes, so a new order shows up here immediately.
*
* Money columns are derived from the ledgers, never typed - Deposit and
* payment are SUMs, Balance is final_amount minus what was paid.
*
* Deposit counts both of them: what the customer put down with us
* (`payments`) and what we put down with the supplier for the same order
* (`supplier_payments`). The supplier screen is the only place a deposit can
* be entered, so counting one ledger left that money off the sheet it was
* entered against.
*/

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";
import Popover from "@mui/material/Popover";
import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from "@mui/material";
import axios from "axios";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import {
  FilterSelect,
  FilterDates,
  optionsFromRows,
  matches,
  inDateRange,
} from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import AddDialog from "components/AddDialog";
import OrderPhoto from "components/OrderPhoto";
import OrderStatusDialog from "components/OrderStatusDialog";
import RowActions from "components/RowActions";
import ensureSupplierForShopCode from "utils/suppliers";
import { boardAction } from "utils/board";
import fetchFirstAmounts from "utils/orderAmounts";
import PAY_TYPES, { payTypeOf } from "utils/payType";
import exportToExcel, { stamp } from "utils/exportExcel";
import { useOpenFromUrl } from "utils/openRecord";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api/v2";

const money = (v) =>
  v === null || v === undefined || v === "" ? "-" : Number(v).toLocaleString("en-US");

/**
 * What was expected to be owed before the amount was agreed: the quoted
 * amount less everything already paid on the order - the deposit and the
 * payments, ours to the supplier as well as the customer's to us.
 *
 * The server works it out, because netting off what we paid the supplier
 * needs figures this list does not carry, and sends null for an order that
 * was never quoted. The subtraction below is only for a server that does
 * not send it yet, where the deposit is all there is to go on.
 */
const expectedBalance = (o) => {
  if (o.expected_balance !== undefined) return o.expected_balance;

  return Number(o.first_amount) > 0 ? Number(o.first_amount) - (Number(o.deposit) || 0) : null;
};

/**
 * Everything paid on an order: the deposit and the payments together, ours
 * to the supplier included - what the row's own two columns add up to, and
 * what BALANCE is measured against.
 *
 * `paid` is the customer's side alone. It is the whole story only on a
 * server that does not send `payment` yet, where it is also all there is.
 */
const paidOn = (o) =>
  o.payment === undefined
    ? Number(o.paid) || 0
    : (Number(o.deposit) || 0) + (Number(o.payment) || 0);

/**
 * What an order is still missing of the three it needs before it can go in
 * a container - the rule the notifications list uses, so the warning after
 * saving and the notice that follows it never disagree: 0 is not a figure.
 */
const MISSING_LABELS = {
  total_cbm: "الحجم (T.CBM)",
  total_kg: "الوزن (T.KG)",
  delivery_date: "تاريخ التسليم",
};

const missingOf = (o) =>
  [
    !(Number(o.total_cbm) > 0) && "total_cbm",
    !(Number(o.total_kg) > 0) && "total_kg",
    !String(o.delivery_date || "").trim() && "delivery_date",
  ].filter(Boolean);

/**
 * The columns that hold figures - each one totalled above its heading and
 * sortable from it. `value` is what the cell shows, so the total and the
 * order are always of the number on screen and never of a neighbouring
 * field that happens to share a name.
 */
const NUMERIC = [
  /**
   * The measurements. A 0 is what the server stores for "never entered", so
   * it counts as no figure at all: shown as "-", sorted last, and adding
   * nothing to the total - the same rule the missing-data notice uses.
   */
  {
    key: "cbm",
    label: "T.CBM",
    value: (o) => (Number(o.total_cbm) > 0 ? Number(o.total_cbm) : null),
  },
  { key: "kg", label: "T.KG", value: (o) => (Number(o.total_kg) > 0 ? Number(o.total_kg) : null) },
  /**
   * The cartons, beside the other two measurements. Being on this list is
   * what gives the column its heading, its sort, its total across the top
   * and its place in the Excel file - and the same rule as its neighbours:
   * an order nobody has counted shows "-" and adds nothing.
   */
  {
    key: "ctn",
    label: "T.CTN",
    value: (o) => (Number(o.total_ctn) > 0 ? Number(o.total_ctn) : null),
  },
  { key: "charged", label: "Final Amount", value: (o) => o.charged },
  { key: "deposit", label: "Deposit", value: (o) => o.deposit },
  { key: "payment", label: "payment", value: (o) => o.payment ?? o.paid },
  // the quoted figure first, then the agreed one
  { key: "expected", label: "Expected Balance", value: expectedBalance },
  { key: "balance", label: "Balance", value: (o) => o.balance },
];

/** Whether an order carries a note worth flagging on its row. */
const hasNote = (o) =>
  Boolean(String(o.notes || "").trim() || String(o.delivery_notes || "").trim());

/** A figure, or null for a cell that has none - never NaN, never a guess. */
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * The table's order: by delivery date, nearest first.
 *
 * The list is read as "what is due next", so the earliest date is at the top
 * and the furthest at the bottom. It used to run the other way - latest
 * first - which put the order due this week under everything due next month.
 *
 * An order with no delivery date cannot be placed on that line, so those go
 * after every dated one rather than being scattered through it by whatever
 * the empty string compares as. Among equal dates - and among the undated -
 * the newer order comes first.
 */
const byDelivery = (a, b) => {
  const da = a.delivery_date || "";
  const db = b.delivery_date || "";

  if (!da !== !db) return da ? -1 : 1;
  if (da !== db) return da.localeCompare(db);

  return String(b.date || "").localeCompare(String(a.date || ""));
};

/**
 * The order when the shop code heading has been clicked: A to Z, then Z to A.
 *
 * Compared the way a person reads a code - AKH9 before AKH10, and "amj 09"
 * beside "AMJ 09" - rather than character by character. An order with no
 * code goes last either way, and equal codes fall back to the delivery date
 * so the list never shuffles.
 */
const byShopCode = (dir) =>
  function compareCodes(a, b) {
    const x = String(a.shop_code || "").trim();
    const y = String(b.shop_code || "").trim();

    if (!x !== !y) return x ? -1 : 1;

    const order = x.localeCompare(y, undefined, { numeric: true, sensitivity: "base" });

    if (order !== 0) return dir === "asc" ? order : -order;

    return byDelivery(a, b);
  };

/**
 * The order when a figure column has been clicked. Cells with no figure go
 * last in both directions - an unquoted order is not "the smallest" - and
 * equal figures fall back to the delivery date, so the list never shuffles.
 */
const byNumber = (column, dir) =>
  function compareFigures(a, b) {
    const x = num(column.value(a));
    const y = num(column.value(b));

    if ((x === null) !== (y === null)) return x === null ? 1 : -1;
    if (x !== null && x !== y) return dir === "asc" ? x - y : y - x;

    return byDelivery(a, b);
  };

/**
 * Whether an order carries a given status.
 *
 * Compared loosely on the name, unlike the Status filter, because a tab
 * pinned to one status names it in the code: the statuses are rows the
 * office maintains, and LOADED being renamed "Loaded" one afternoon must
 * not quietly empty the tab that exists to show them.
 */
const isStatus = (order, name) =>
  String(order.status ?? "")
    .trim()
    .toLowerCase() === String(name).trim().toLowerCase();

/**
 * The orders table.
 *
 * `lockedStatus` makes it one status's screen - the Loaded tab is this page
 * pinned to LOADED. The status filter goes with it, because a tab that says
 * Loaded and a dropdown that can turn it into something else are two
 * different answers to the same question.
 *
 * `excludeStatus` is the other side of that: the Orders tab leaves LOADED
 * out, because those have a tab of their own and were only doubling this
 * list up. Its status filter stays, offering the statuses the tab shows.
 */
function Orders({ lockedStatus, excludeStatus, title }) {
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  // what a just-created order is still missing, shown until dismissed
  const [missingNotice, setMissingNotice] = useState(null);
  const [editingId, setEditingId] = useState(null);
  // a notice about an order lands here as /orders?open=512 and gets the
  // order's own form - which loads by id, so it opens even for an order this
  // tab's list leaves out (a LOADED one, from the Orders tab)
  useOpenFromUrl(setEditingId);
  // the order whose status is being changed, from the row's own button
  const [statusFor, setStatusFor] = useState(null);
  // ids ticked in the table, kept as a Set for O(1) lookups while rendering
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  // column filters, all applied on top of the free-text search
  const [status, setStatus] = useState("");
  // every status the system has, for the bulk change
  const [statuses, setStatuses] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [container, setContainer] = useState("");
  const [customer, setCustomer] = useState("");
  const [settled, setSettled] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // a clicked figure column, {key, dir}; null is the delivery-date order
  const [sortBy, setSortBy] = useState(null);
  // the order whose note is open, and the icon it was opened from
  const [noteFor, setNoteFor] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      // the endpoint defaults to a small page; ask for the lot and let the
      // client-side pager and search work across all of it
      const res = await fetch(`${API}/orders?limit=2000`);
      const json = await res.json();
      const rows = Array.isArray(json) ? json : [];

      /**
       * first_amount drives the Expected Balance column but is not on the
       * list response, so it is recovered per order until the server sends
       * it. Orders that already carry it cost nothing.
       */
      const firstById = await fetchFirstAmounts(rows);

      // null rather than undefined, so the column can tell "not known" from
      // a genuine zero
      const firstFor = (o) => {
        if (o.first_amount !== undefined && o.first_amount !== null) return Number(o.first_amount);
        return firstById.has(o.id) ? firstById.get(o.id) : null;
      };

      setOrders(rows.map((o) => ({ ...o, first_amount: firstFor(o) })));
    } catch (e) {
      setOrders([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();

    // the statuses are a short fixed list; a failure here only costs the
    // bulk control, so it must not disturb the page
    fetch(`${API}/order-options`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setStatuses(Array.isArray(d?.statuses) ? d.statuses : []))
      .catch(() => setStatuses([]));
  }, []);

  /**
   * Column filters first, then the free-text search over what is left, so
   * the search box narrows the visible tab rather than the whole table.
   */
  // the tab's own rows: one status when pinned, all but one when excluding
  const pool = orders.filter((o) => {
    if (lockedStatus && !isStatus(o, lockedStatus)) return false;
    if (excludeStatus && isStatus(o, excludeStatus)) return false;
    return true;
  });

  const narrowed = pool.filter((o) => {
    if (!matches(o.status, status)) return false;
    if (!matches(o.container, container)) return false;
    if (!matches(o.customer, customer)) return false;
    if (!inDateRange(o.date, from, to)) return false;

    if (settled === "unpaid" && !(Number(o.balance) > 0)) return false;
    if (settled === "settled" && Number(o.balance) > 0) return false;
    if (settled === "overpaid" && !(Number(o.balance) < 0)) return false;

    return true;
  });

  const filteredRows = filterRows(narrowed, query);
  /**
   * One click sorts, the second turns it round, the third hands the table
   * back to the delivery date - so there is always a way home.
   *
   * Which way the first click goes depends on the column: a figure is wanted
   * highest first, a shop code A to Z.
   */
  const toggleSort = (key, first = "desc") =>
    setSortBy((prev) => {
      if (!prev || prev.key !== key) return { key, dir: first };
      if (prev.dir === first) return { key, dir: first === "desc" ? "asc" : "desc" };
      return null;
    });

  const sortColumn = sortBy ? NUMERIC.find((c) => c.key === sortBy.key) : null;

  let compare = byDelivery;
  if (sortBy?.key === "shop_code") compare = byShopCode(sortBy.dir);
  else if (sortColumn) compare = byNumber(sortColumn, sortBy.dir);

  // sorted after filtering, so a search or a filter never reshuffles the rule
  const sortedRows = [...filteredRows].sort(compare);

  // every row the filters let through, not just this page - a total of 25
  // rows out of 473 is a number nobody asked for
  const columnTotals = {};
  NUMERIC.forEach((c) => {
    columnTotals[c.key] = filteredRows.reduce((sum, o) => sum + (num(c.value(o)) || 0), 0);
  });
  // 100 to a page: this list is worked through in long runs, and 25 at a
  // time meant paging every few seconds
  const pager = usePagination(sortedRows, 100);

  /**
   * The list as an Excel file: every row the filters and the search let
   * through, in the order the table has them - not the page that is open.
   *
   * The same headings and figures as the table, plus what the table only
   * shows as a colour or an icon: who pays the supplier, and the notes.
   */
  const exportExcel = () => {
    const slug = String(title || "orders")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

    exportToExcel({
      fileName: `${slug || "orders"}-${stamp()}`,
      sheetName: title || "Orders",
      rows: sortedRows,
      columns: [
        { header: "ID", value: (o) => o.id },
        { header: "Shop code", value: (o) => o.shop_code },
        { header: "Customer", value: (o) => o.customer },
        { header: "Order date", value: (o) => o.date },
        { header: "Delivery date", value: (o) => o.delivery_date },
        { header: "Type of goods", value: (o) => o.goods },
        { header: "CON NO", value: (o) => o.container },
        { header: "Status", value: (o) => o.status },
        { header: "Pay type", value: (o) => payTypeOf(o.pay_type)?.label },
        ...NUMERIC.map((c) => ({ header: c.label, value: (o) => num(c.value(o)) })),
        { header: "Note", value: (o) => o.notes },
        { header: "Delivery note", value: (o) => o.delivery_notes },
      ],
    });
  };

  // totals of what is on screen, so a filtered view still answers "how much"
  const shown = filteredRows.reduce(
    (acc, r) => ({
      charged: acc.charged + (Number(r.charged) || 0),
      paid: acc.paid + paidOn(r),
      balance: acc.balance + (Number(r.balance) || 0),
    }),
    { charged: 0, paid: 0, balance: 0 }
  );

  const cell = { fontSize: 13, whiteSpace: "nowrap" };

  // select-all applies to the CURRENT page only - ticking a header box and
  // silently selecting 589 hidden rows is how people delete things by mistake
  const pageIds = pager.pageRows.map((o) => o.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  /**
   * Copy an order. The server assigns the next code and the customer's next
   * shop code, and deliberately does not copy the payments - that money
   * belongs to the original order.
   */
  const duplicateOne = async (o) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/orders/${o.id}/duplicate`);

      // the copy gets a new shop code, so it needs its supplier too
      const newCode = res?.data?.order?.shop_code;
      if (newCode) await ensureSupplierForShopCode(newCode);

      await fetchData();
      // eslint-disable-next-line no-alert
      window.alert(res?.data?.message || "Duplicated");
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not duplicate");
    }
    setBusy(false);
  };

  const deleteOne = async (o) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete order ${o.shop_code}?`)) return;

    setBusy(true);
    try {
      await axios.delete(`${API}/orders/${o.id}`);
      await fetchData();
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(o.id);
        return next;
      });
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
    setBusy(false);
  };

  /**
   * Set one status on everything ticked.
   *
   * Confirmed first: it is one click over an arbitrary number of orders and
   * there is no undo, which is the same reason the delete asks.
   */
  const applyStatus = async () => {
    const ids = [...selected];
    const chosen = statuses.find((s) => String(s.id) === String(bulkStatus));
    if (!chosen || ids.length === 0) return;

    // eslint-disable-next-line no-alert
    if (!window.confirm(`Set ${ids.length} order(s) to ${chosen.name}?`)) return;

    setBusy(true);
    try {
      await axios.post(`${API}/orders/bulk-status`, { ids, status_id: chosen.id });
      setSelected(new Set());
      setBulkStatus("");
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not change the status");
    }
    setBusy(false);
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${ids.length} order(s)?`)) return;

    setBusy(true);
    try {
      await axios.post(`${API}/orders/bulk-delete`, { ids });
      setSelected(new Set());
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
    setBusy(false);
  };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <Card>
          {missingNotice && (
            <SuiBox px={3} pt={2}>
              <Alert severity="warning" onClose={() => setMissingNotice(null)}>
                {`انحفظت الطلبية ${missingNotice.shop || ""} - ناقصها: ${missingNotice.missing
                  .map((k) => MISSING_LABELS[k])
                  .join("، ")}. رح تضل بالإشعارات لحد ما تنعبى.`}
              </Alert>
            </SuiBox>
          )}

          {/* title, search, totals, count and the button - one row */}
          <FilterBar
            title={title}
            value={query}
            onChange={setQuery}
            count={filteredRows.length}
            total={pool.length}
            placeholder="Search by shop code, customer or container..."
            actions={
              <>
                {/* what the filters show, as a file */}
                <Button
                  variant="outlined"
                  color="dark"
                  onClick={exportExcel}
                  disabled={sortedRows.length === 0}
                >
                  Export Excel
                </Button>
                <Button variant="contained" color="primary" onClick={() => setAdding(true)}>
                  Add Order
                </Button>
              </>
            }
          >
            {/* the choices come from the rows themselves, so the list can
                never offer a status or container that does not exist */}
            {lockedStatus ? null : (
              <FilterSelect
                label="Status"
                icon="flag"
                value={status}
                onChange={setStatus}
                // only what this tab can show - no LOADED on the Orders tab
                options={optionsFromRows(pool, "status")}
              />
            )}
            <FilterSelect
              label="Container"
              icon="inventory_2"
              value={container}
              onChange={setContainer}
              options={optionsFromRows(orders, "container")}
            />
            <FilterSelect
              label="Customer"
              icon="person"
              value={customer}
              onChange={setCustomer}
              options={optionsFromRows(orders, "customer")}
            />
            <FilterSelect
              label="Money"
              icon="payments"
              value={settled}
              onChange={setSettled}
              anyLabel="All"
              options={[
                { value: "unpaid", label: "Outstanding" },
                { value: "settled", label: "Settled" },
                { value: "overpaid", label: "Overpaid" },
              ]}
            />
            <FilterDates label="Ordered" from={from} to={to} onFrom={setFrom} onTo={setTo} />

            <SuiTypography variant="caption" sx={{ opacity: 0.75, whiteSpace: "nowrap" }}>
              {`Charged ${money(shown.charged)} · Paid ${money(shown.paid)} · Balance ${money(
                shown.balance
              )}`}
            </SuiTypography>

            {/* what the tinted rows mean. A colour nobody can look up is a
                puzzle rather than information, and the key is two swatches */}
            <SuiBox display="flex" alignItems="center" gap={1.5} sx={{ whiteSpace: "nowrap" }}>
              {PAY_TYPES.map((p) => (
                <SuiBox key={p.value} display="flex" alignItems="center" gap={0.75}>
                  <SuiBox
                    sx={({ palette, functions: { rgba } }) => ({
                      width: 14,
                      height: 14,
                      borderRadius: "3px",
                      backgroundColor: rgba(palette[p.tone].main, 0.25),
                      border: `1px solid ${rgba(palette[p.tone].main, 0.7)}`,
                    })}
                  />
                  <SuiTypography variant="caption" sx={{ opacity: 0.75 }}>
                    {p.label}
                  </SuiTypography>
                </SuiBox>
              ))}
            </SuiBox>
          </FilterBar>

          {/* only appears once something is ticked, so it never takes up
              space during normal browsing */}
          {selected.size > 0 && (
            <SuiBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1.5}
              mx={3}
              mb={2}
              px={2}
              py={1.25}
              sx={({ palette, borders, functions: { rgba } }) => ({
                borderRadius: borders.borderRadius.md,
                backgroundColor: rgba(palette.primary.main, 0.12),
                border: `1px solid ${rgba(palette.primary.main, 0.35)}`,
              })}
            >
              <SuiTypography variant="button" fontWeight="bold">
                {`${selected.size} selected`}
              </SuiTypography>

              <SuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {/* the status change sits with the other things you can do to
                    a selection, rather than in a menu of its own */}
                <TextField
                  select
                  size="small"
                  label="Set status"
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  disabled={busy || statuses.length === 0}
                  sx={{ minWidth: 168, backgroundColor: "white", borderRadius: 1 }}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  {/* a blank first entry would read as a status */}
                  <option value="">Choose...</option>
                  {statuses.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </TextField>

                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={applyStatus}
                  disabled={busy || !bulkStatus}
                >
                  Apply
                </Button>

                <Button size="small" onClick={() => setSelected(new Set())} disabled={busy}>
                  Clear selection
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={deleteSelected}
                  disabled={busy}
                >
                  Delete selected
                </Button>
              </SuiBox>
            </SuiBox>
          )}

          {loading ? (
            <CargoLoader label="Loading orders" />
          ) : (
            <>
              <SuiBox sx={{ overflowX: "auto" }}>
                {/* tighter than the theme's roomy default: this is the
                    longest list in the system and is scanned, not read */}
                <Table
                  sx={{
                    minWidth: 1180,
                    // narrow columns: the theme's 20px either side of every
                    // cell was most of the table's width on its own
                    "& .MuiTableCell-root": { px: 1 },
                    "& .MuiTableBody-root .MuiTableCell-root": { py: 0.75 },
                    // a long heading breaks onto two lines rather than
                    // widening its whole column to fit on one
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      whiteSpace: "normal",
                      fontSize: 11,
                      letterSpacing: "0.04em",
                      lineHeight: 1.25,
                    },
                  }}
                >
                  <TableHead>
                    {/* the sum of each figure column, over everything the
                        filters and the search let through */}
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        align="right"
                        sx={{ fontSize: 12, fontWeight: 700, textTransform: "none", py: 1 }}
                      >
                        {`Total (${filteredRows.length})`}
                      </TableCell>
                      {NUMERIC.map((c) => (
                        <TableCell
                          key={c.key}
                          align="center"
                          sx={({ palette }) => ({
                            py: 1,
                            fontSize: 13,
                            fontWeight: 800,
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            fontVariantNumeric: "tabular-nums",
                            color:
                              c.key === "balance" && columnTotals.balance < 0
                                ? palette.error.main
                                : palette.dark.main,
                          })}
                        >
                          {money(columnTotals[c.key])}
                        </TableCell>
                      ))}
                      <TableCell sx={{ py: 1 }} />
                    </TableRow>

                    <TableRow>
                      <TableCell align="center" padding="checkbox">
                        <Checkbox
                          checked={allOnPageSelected}
                          indeterminate={
                            !allOnPageSelected && pageIds.some((id) => selected.has(id))
                          }
                          onChange={togglePage}
                        />
                      </TableCell>
                      <IdHead />
                      {/* A to Z on the first click, Z to A on the second, and
                          back to the delivery date on the third */}
                      <TableCell
                        align="center"
                        sortDirection={sortBy?.key === "shop_code" ? sortBy.dir : false}
                      >
                        <TableSortLabel
                          active={sortBy?.key === "shop_code"}
                          direction={sortBy?.key === "shop_code" ? sortBy.dir : "asc"}
                          onClick={() => toggleSort("shop_code", "asc")}
                        >
                          shop code
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Order Date</TableCell>
                      <TableCell align="center">Delivery Date</TableCell>
                      <TableCell align="center">Type of goods</TableCell>
                      <TableCell align="center">CON NO</TableCell>
                      <TableCell align="center">STATUS</TableCell>
                      {NUMERIC.map((c) => {
                        const on = sortBy?.key === c.key;

                        return (
                          <TableCell
                            key={c.key}
                            align="center"
                            sortDirection={on ? sortBy.dir : false}
                          >
                            <TableSortLabel
                              active={on}
                              direction={on ? sortBy.dir : "desc"}
                              onClick={() => toggleSort(c.key)}
                            >
                              {c.label}
                            </TableSortLabel>
                          </TableCell>
                        );
                      })}
                      <TableCell align="center">Operations</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pager.pageRows.map((o) => (
                      /**
                       * The row is tinted by who settles with the supplier -
                       * the same two colours as the tabs on the order form.
                       * A tint rather than a column: it is true of the whole
                       * row, and the table is wide enough already.
                       *
                       * An order with no answer yet keeps the plain row, so
                       * the colour always means something was chosen.
                       */
                      <TableRow
                        key={o.id}
                        selected={selected.has(o.id)}
                        sx={({ palette, functions: { rgba } }) => {
                          const pay = payTypeOf(o.pay_type);
                          if (!pay) return {};

                          /**
                           * The theme stripes even rows with a selector
                           * (:nth-of-type(even):not(.MuiTableRow-head)) that
                           * outranks a plain background here - so every
                           * second tinted row showed the beige stripe instead
                           * of its colour. Matching that selector, doubled,
                           * puts the tint back on both parities.
                           *
                           * A ticked row is left alone, so the selection
                           * highlight still shows which rows are chosen.
                           */
                          const row = "&&:not(.Mui-selected)";
                          const even =
                            "&&:nth-of-type(even):not(.MuiTableRow-head):not(.Mui-selected)";

                          return {
                            [`${row}, ${even}`]: {
                              backgroundColor: rgba(palette[pay.tone].main, 0.08),
                            },
                            [`${row}:hover, ${even}:hover`]: {
                              backgroundColor: rgba(palette[pay.tone].main, 0.14),
                            },
                          };
                        }}
                      >
                        <TableCell align="center" padding="checkbox">
                          <Checkbox checked={selected.has(o.id)} onChange={() => toggleRow(o.id)} />
                        </TableCell>
                        <IdCell value={o.id} />
                        <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                          <SuiBox
                            component="span"
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                          >
                            {/* its own element, so the code is findable as itself */}
                            <span>{o.shop_code ?? "-"}</span>

                            {/* only on a row that has a note: the icon is the sign */}
                            {hasNote(o) && (
                              <SuiBox
                                component="button"
                                type="button"
                                aria-label={`Note on ${o.shop_code}`}
                                title="Show the note"
                                onClick={(e) => setNoteFor({ anchor: e.currentTarget, order: o })}
                                sx={({ palette, functions: { rgba } }) => ({
                                  display: "grid",
                                  placeItems: "center",
                                  width: 22,
                                  height: 22,
                                  p: 0,
                                  border: 0,
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  backgroundColor: rgba(palette.warning.main, 0.18),
                                  color: palette.warning.focus || palette.warning.main,
                                  "&:hover": { backgroundColor: rgba(palette.warning.main, 0.32) },
                                })}
                              >
                                <Icon sx={{ fontSize: "15px !important" }}>sticky_note_2</Icon>
                              </SuiBox>
                            )}
                          </SuiBox>
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.customer ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.date ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={{ ...cell, fontWeight: 600 }}>
                          {o.delivery_date || "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.goods ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.container ?? "-"}
                        </TableCell>

                        <TableCell align="center" sx={cell}>
                          {o.status ? (
                            <Chip
                              label={o.status}
                              size="small"
                              sx={({ palette, functions: { rgba } }) => ({
                                fontWeight: 700,
                                backgroundColor: rgba(palette.primary.main, 0.16),
                                color: palette.dark.main,
                              })}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>

                        <TableCell align="center" sx={cell}>
                          {Number(o.total_cbm) > 0 ? money(o.total_cbm) : "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {Number(o.total_kg) > 0 ? money(o.total_kg) : "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {Number(o.total_ctn) > 0 ? money(o.total_ctn) : "-"}
                        </TableCell>

                        <TableCell align="center" sx={cell}>
                          {money(o.charged)}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {money(o.deposit)}
                        </TableCell>
                        {/* what was paid on top of the deposit, ours to the
                            supplier included. `paid` is the customer's side
                            alone, which is what BALANCE is measured against
                            - a server that does not send `payment` yet has
                            only that to show. */}
                        <TableCell align="center" sx={cell}>
                          {money(o.payment ?? o.paid)}
                        </TableCell>

                        {/* the supplier column is the sum of these, so the
                            two screens net off the same thing - see
                            Supplier::scopeWithBalances */}
                        <TableCell align="center" sx={{ ...cell, opacity: 0.85 }}>
                          {money(expectedBalance(o))}
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={({ palette }) => ({
                            ...cell,
                            fontWeight: 700,
                            // a negative balance means the customer overpaid
                            color: Number(o.balance) < 0 ? palette.error.main : palette.dark.main,
                          })}
                        >
                          {money(o.balance)}
                        </TableCell>

                        <TableCell align="center" sx={cell}>
                          <SuiBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.75,
                            }}
                          >
                            {/* attaching it marks the order RECEIVED, so it
                                belongs where the other row actions are */}
                            <OrderPhoto
                              orderId={o.id}
                              photoUrl={o.photo_url}
                              onChanged={fetchData}
                            />
                            <RowActions
                              actions={[
                                boardAction("order", o, fetchData),
                                {
                                  /* the status is not on the create form -
                                     this is where an order is moved on */
                                  icon: "flag",
                                  label: "Change status",
                                  tone: "info",
                                  disabled: busy,
                                  onClick: () => setStatusFor(o),
                                },
                                {
                                  icon: "edit",
                                  label: "Edit order",
                                  onClick: () => setEditingId(o.id),
                                },
                                {
                                  icon: "content_copy",
                                  label: "Duplicate order",
                                  tone: "primary",
                                  disabled: busy,
                                  onClick: () => duplicateOne(o),
                                },
                                {
                                  icon: "delete",
                                  label: "Delete order",
                                  tone: "danger",
                                  disabled: busy,
                                  onClick: () => deleteOne(o),
                                },
                              ]}
                            />
                          </SuiBox>
                        </TableCell>
                      </TableRow>
                    ))}

                    {filteredRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={16} align="center" sx={{ py: 4 }}>
                          <SuiTypography variant="button" color="text">
                            No orders
                          </SuiTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </SuiBox>
              <Pagination {...pager} />
            </>
          )}
        </Card>
      </SuiBox>

      <AddDialog
        open={adding}
        entity="order"
        onClose={() => setAdding(false)}
        onCreated={(saved) => {
          fetchData();
          // a customer_pay order is told at once what it is missing, and the
          // notifications list keeps asking; a we_pay order has its own
          // measure notice for that
          const missing = saved?.pay_type === "customer_pay" ? missingOf(saved) : [];
          setMissingNotice(missing.length ? { shop: saved?.shop_code, missing } : null);
        }}
      />

      {/* the note on one order, opened from the icon beside its shop code */}
      <Popover
        open={Boolean(noteFor)}
        anchorEl={noteFor?.anchor}
        onClose={() => setNoteFor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {noteFor && (
          <SuiBox p={2} sx={{ maxWidth: 360 }}>
            <SuiTypography sx={{ fontSize: 13, fontWeight: 800, mb: 1 }}>
              {`${noteFor.order.shop_code || ""} - note`}
            </SuiTypography>

            {String(noteFor.order.notes || "").trim() && (
              // dir="auto": most notes here are Arabic, some are English
              <SuiTypography dir="auto" sx={{ fontSize: 14, whiteSpace: "pre-wrap", mb: 1 }}>
                {noteFor.order.notes}
              </SuiTypography>
            )}

            {String(noteFor.order.delivery_notes || "").trim() && (
              <>
                <SuiTypography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.7 }}>
                  Delivery note
                </SuiTypography>
                <SuiTypography dir="auto" sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
                  {noteFor.order.delivery_notes}
                </SuiTypography>
              </>
            )}
          </SuiBox>
        )}
      </Popover>

      {/* one row's status, from the flag button on that row */}
      <OrderStatusDialog
        open={Boolean(statusFor)}
        order={statusFor}
        statuses={statuses}
        onClose={() => setStatusFor(null)}
        onSaved={fetchData}
      />

      {/* same dialog, loaded with an existing order */}
      <AddDialog
        open={Boolean(editingId)}
        entity="order"
        recordId={editingId}
        /* the photo is not a form field - it uploads on its own and sets the
           status, so it cannot wait for Save */
        extra={
          editingId ? (
            <OrderPhoto
              orderId={editingId}
              photoUrl={orders.find((o) => String(o.id) === String(editingId))?.photo_url || null}
              variant="panel"
              onChanged={fetchData}
            />
          ) : null
        }
        onClose={() => setEditingId(null)}
        onCreated={fetchData}
      />

      <Footer />
    </DashboardLayout>
  );
}

Orders.defaultProps = { lockedStatus: "", excludeStatus: "", title: "Orders" };

Orders.propTypes = {
  // pin the page to one status, matched on the status name
  lockedStatus: PropTypes.string,
  // leave one status out, matched the same way - LOADED, on the Orders tab
  excludeStatus: PropTypes.string,
  title: PropTypes.string,
};

export default Orders;
